import { useEffect, useRef, useState } from 'react';
import cm from 'classnames';

import { StoryBoard } from 'services/storyboard.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { selectHelpMode } from 'components/app/_mediator/app-data.slice';
import { useWsMarkersCb } from 'components/app/audio-track/_mediator/use-ws-markers-cb';
import {
  markersConversion,
  getMarkerIndexesExcluding,
} from 'components/app/editor/_mediator/editor-markers/editor-markers.helper';
import { useMarkersCb } from 'components/app/editor/_mediator/editor-markers/use-markers-cb';
import { handleMarkers } from 'components/app/editor/_mediator/editor.slice';

import { ReactComponent as PlusIcon } from 'assets/ui/plus.svg';
import { ReactComponent as TrashIcon } from 'assets/ui/trash.svg';
import { ReactComponent as KeepSelectedIcon } from 'assets/ui/keep-selected.svg';
import { Tooltip } from 'components/UI/tooltip/tooltip';
import { WsButton } from './elements/ws-button';
import styles from './_ws-header.module.scss';

export const WsHeaderMarkers = ({ ws }: { ws: WaveSurfer }) => {
  const { shadeCullCandidates } = useWsMarkersCb(ws);
  const { delMarkers, addMarkers } = useMarkersCb();
  const { clipsMap, selectedMarkers, markers } = useAppSelector((state) => state.editor);
  const storyConfig = useAppSelector((state) => state.editor.storyConfig);
  const helpMode = useAppSelector(selectHelpMode);
  const [isCullBtnExpanded, setIsCullBtnExpanded] = useState(false);
  const [cullMarkersNth, setCullMarkersNth] = useState<number | null>(null);
  const dispatch = useAppDispatch();

  const unselectedMarkers = markers?.length ? getMarkerIndexesExcluding(markers.length, selectedMarkers) : [];

  const cullMarkersInputRef = useRef<HTMLInputElement>(null);
  const cullResultRef = useRef<
    | {
        markers: number[];
        storyBoard: Partial<StoryBoard>;
      }
    | undefined
  >(undefined);

  const handleCullMarkersBtn = (state: boolean) => {
    setIsCullBtnExpanded(state);

    if (!cullMarkersInputRef.current) {
      setCullMarkersNth(null);
      return;
    }

    if (state) {
      cullMarkersInputRef.current.focus();
      return;
    }

    cullMarkersInputRef.current.blur();
    setCullMarkersNth(null);
    cullResultRef.current = undefined;
    shadeCullCandidates();
  };

  const handleCullMarkersNth = (value: number | null) => {
    setCullMarkersNth(value);

    if (!value) shadeCullCandidates();
    if (!markers || !clipsMap || !value) return;

    const cullRange: [number, number] = [
      selectedMarkers[0] + 1,
      selectedMarkers.length === 1 ? markers.length : selectedMarkers[selectedMarkers.length - 1] + 1,
    ];

    const removedMarkers: number[] = getRemovedMarkersAfterCull(markers, value, cullRange);
    const res = delMarkers({ selectedMarkers: removedMarkers, dispatch: false });

    if (res) {
      cullResultRef.current = { ...res };
      shadeCullCandidates({
        removedMarkers,
        culledMarkers: res.markers,
      });
    }
  };

  const handleCullMarkersApprove = () => {
    if (cullResultRef.current) {
      dispatch(
        handleMarkers({
          data: { markers: cullResultRef.current.markers, storyBoard: cullResultRef.current.storyBoard },
          backupParams: { title: 'Cull Markers' },
        })
      );
    }

    handleCullMarkersBtn(false);
    cullResultRef.current = undefined;
  };

  const handleAdd = () => {
    if (!storyConfig || !markers) return;
    const { transitionTime, clipTransitionAlignment } = storyConfig;
    addMarkers(markersConversion('fromBE', [ws.getCurrentTime()], transitionTime, clipTransitionAlignment));
  };

  const handleDeleteOthers = () => {
    if (!unselectedMarkers.length) return;
    delMarkers({
      selectedMarkers: unselectedMarkers,
      retainMarkerIndices: selectedMarkers,
    });
  };

  useEffect(() => {
    if (!selectedMarkers.length && isCullBtnExpanded) handleCullMarkersBtn(false);
  }, [selectedMarkers]);

  return (
    <div className={styles.WsHeader__markers}>
      {selectedMarkers.length ? (
        <>
          <span className={styles.WsHeader__markersInfo}>Selected Markers: {selectedMarkers.length}</span>
          <div className={cm(styles.WsHeader__cull, { [styles.expanded]: isCullBtnExpanded })}>
            <WsButton onClick={() => handleCullMarkersBtn(true)}>♖</WsButton>
            <div className={styles.WsHeader__cullWrapper}>
              <input
                value={cullMarkersNth ?? ''}
                onChange={(e) => handleCullMarkersNth(e.target.value.length ? Number(e.target.value) : null)}
                placeholder="Nth"
                type="number"
                ref={cullMarkersInputRef}
              />
              <button
                disabled={!selectedMarkers.length || !cullMarkersNth || cullMarkersNth <= 0}
                onClick={handleCullMarkersApprove}
              >
                ✓
              </button>
              <button onClick={() => handleCullMarkersBtn(false)}>×</button>
            </div>
          </div>
          <WsButton
            className={styles.WsHeader__markersKeep}
            onClick={handleDeleteOthers}
            disabled={!unselectedMarkers.length}
            tip="Keep selected markers (delete others)"
          >
            <KeepSelectedIcon />
          </WsButton>
          <WsButton className={styles.WsHeader__markersDel} onClick={() => delMarkers()} tip="Delete selected markers">
            <TrashIcon />
          </WsButton>
        </>
      ) : (
        <>
          <Tooltip tip="Number of beat markers on the music track" showTooltip={helpMode}>
            <span className={styles.WsHeader__markersInfo}>Beat Markers: {markers?.length}</span>
          </Tooltip>
          <WsButton className={styles.WsHeader__markersAdd} onClick={handleAdd} tip="Add beat marker">
            <PlusIcon />
          </WsButton>
        </>
      )}
    </div>
  );
};

/* H E L P E R S */

export const getRemovedMarkersAfterCull = (markers: MarkerId[], N: number, range: [number, number]) => {
  const nth: MarkerId[] = [];
  const [start, end] = range;
  let count = 1;

  markers.forEach((_, index) => {
    if (index >= start && index < end) {
      if (count < N) {
        count += 1;
        nth.push(index);
      } else count = 1;
    }
  });
  return nth;
};
